import { Box, Button, IconButton, Stack, TextField } from '@mui/material';
import { FilterKeys, GroupType } from '@schooly/api';
import {
  CrossIcon,
  Loading,
  ModalConfirm,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spin,
  TruncatedTagSelect,
} from '@schooly/style';
import { FC, useCallback } from 'react';
import { Controller } from 'react-hook-form-lts';
import { useIntl } from 'react-intl';

import {
  MarkbookFilterForm,
  useMarkbookFilterContext,
} from '../../../../context/markbook/MarkbookFilterContext';
import { useMarkbookFilterSummary } from './helpers';

const COMMON_TRUNCATED_TOOLTIP_PROPS = {
  componentsProps: { tooltip: { sx: { maxWidth: 'unset' } } },
};

type MarkbookSaveModalProps = {
  opened: boolean;
  onClose: () => void;
  onSave: () => void;
  isSaving: boolean;
};

export const MarkbookSaveModal: FC<MarkbookSaveModalProps> = ({
  opened,
  onClose,
  onSave,
  isSaving,
}) => {
  const { formatMessage } = useIntl();
  const { form } = useMarkbookFilterContext();

  const date: MarkbookFilterForm['date'] = form.watch('date');
  const groupType: MarkbookFilterForm['group_type'] = form.watch('group_type');
  const groupIds: MarkbookFilterForm['group'] = form.watch('group');
  const ageGroupIds: MarkbookFilterForm['age_group'] = form.watch('age_group');
  const subjectIds: MarkbookFilterForm['subject'] = form.watch('subject');
  const tutorGroup: MarkbookFilterForm['tutor_group'] = form.watch('tutor_group');
  const [aolIds, evaluationTypes]: MarkbookFilterForm['aol_evaluation_type'] =
    form.watch('aol_evaluation_type');

  const {
    dateLabel,
    tutorFeedbackLabel,
    tutorGroupLabel,
    subjectTags,
    groupTags,
    ageGroupTags,
    aolTags,
    isLoading,
  } = useMarkbookFilterSummary({
    filter: {
      date,
      groupIds: groupType === FilterKeys.Group ? groupIds : [],
      ageGroupIds: groupType === FilterKeys.AgeGroup ? ageGroupIds : [],
      groupTypes: tutorGroup ? [GroupType.TutorGroup] : [],
      subjectIds,
      aolIds,
      evaluationTypes,
    },
    tagProps: { size: 'small', sx: { maxWidth: 200 } },
  });

  const onSubmit = useCallback(async () => {
    const isValid = await form.trigger('name');
    if (!isValid) return;
    onSave();
  }, [form, onSave]);

  const step1Items = [...(dateLabel ? [dateLabel] : []), ...ageGroupTags, ...groupTags];
  const step2Items = [
    ...(tutorGroupLabel ? [tutorGroupLabel] : []),
    ...subjectTags,
    ...(tutorFeedbackLabel ? [tutorFeedbackLabel] : []),
    ...aolTags,
  ];

  return (
    <ModalConfirm open={opened} onClose={onClose} fullWidth>
      <ModalHeader active title={formatMessage({ id: 'assessments-markbook-SaveMarkbook' })}>
        <IconButton onClick={onClose}>
          <CrossIcon />
        </IconButton>
      </ModalHeader>
      <ModalContent active sx={{ height: 164 }}>
        {isLoading ? (
          <Loading />
        ) : (
          <Stack>
            <Box sx={{ height: 72 }}>
              <Controller
                control={form.control}
                name="name"
                rules={{
                  required: true,
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    error={!!fieldState.error}
                    placeholder={formatMessage({ id: 'assessments-markbook-Name' })}
                    label={formatMessage({ id: 'assessments-markbook-Name' })}
                    fullWidth
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Box>

            <Stack width={300} gap={0.5} minHeight={40}>
              <TruncatedTagSelect
                items={step1Items}
                spacing={2}
                tooltipProps={COMMON_TRUNCATED_TOOLTIP_PROPS}
              />
              <TruncatedTagSelect
                items={step2Items}
                spacing={2}
                tooltipProps={COMMON_TRUNCATED_TOOLTIP_PROPS}
              />
            </Stack>
          </Stack>
        )}
      </ModalContent>
      <ModalFooter active>
        <Stack direction="row" gap={1}>
          <Button variant="outlined" onClick={onClose} disabled={isSaving}>
            {formatMessage({ id: 'action-Cancel' })}
          </Button>

          <Button onClick={onSubmit} disabled={isSaving} endIcon={isSaving ? <Spin /> : undefined}>
            {formatMessage({ id: 'assessments-markbook-saveAndView' })}
          </Button>
        </Stack>
      </ModalFooter>
    </ModalConfirm>
  );
};
